import { useCallback, useEffect, useState } from "react";
import { api, money, compact } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Stat, PageHeader, profitTone } from "@/components/Stat";
import { TrendingUp, TrendingDown, Car, Wallet, AlertCircle, DollarSign, Package, Truck } from "lucide-react";
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, BarChart, Bar, Legend } from "recharts";

const tip = { contentStyle: { backgroundColor: "hsl(var(--card))", border: "1px solid hsl(var(--border))", borderRadius: 8 }, formatter: (v) => money(v) };
const axis = { stroke: "hsl(var(--muted-foreground))", fontSize: 12 };

export default function Dashboard() {
  const [area, setArea] = useState("all");
  const [start, setStart] = useState("");
  const [end, setEnd] = useState("");
  const [data, setData] = useState(null);

  const load = useCallback(() => {
    api.get("/dashboard", { params: { area, start: start || undefined, end: end || undefined } }).then((r) => setData(r.data));
  }, [area, start, end]);
  useEffect(() => { load(); }, [load]);

  if (!data) return <div className="text-muted-foreground">Yükleniyor...</div>;
  const k = data.kpis;
  const empty = k.transaction_count === 0 && k.vehicle_count === 0;

  return (
    <div className="space-y-6" data-testid="dashboard-page">
      <PageHeader title="Genel Bakış" subtitle="Tüm rakamlar merkezi finansal hareketlerden hesaplanır.">
        <Select value={area} onValueChange={setArea}>
          <SelectTrigger className="w-44" data-testid="dashboard-area-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tüm İş Alanları</SelectItem>
            {["Genel", "Otomotiv", "İnşaat", "Hafriyat"].map((a) => <SelectItem key={a} value={a}>{a}</SelectItem>)}
          </SelectContent>
        </Select>
        <Input type="date" className="w-40" value={start} onChange={(e) => setStart(e.target.value)} data-testid="dashboard-start" />
        <Input type="date" className="w-40" value={end} onChange={(e) => setEnd(e.target.value)} data-testid="dashboard-end" />
      </PageHeader>

      {empty && (
        <Card className="p-6 flex flex-col md:flex-row md:items-center gap-4 justify-between border-dashed" data-testid="empty-state">
          <div>
            <div className="font-display font-semibold">Henüz kayıt yok</div>
            <p className="text-sm text-muted-foreground">Araç, proje veya hafriyat işi ekleyerek başlayın.</p>
          </div>
        </Card>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <Stat label="Toplam Gelir" value={money(k.total_revenue)} icon={DollarSign} tone="blue" testId="kpi-revenue" />
        <Stat label="Toplam Gider" value={money(k.total_expense)} icon={TrendingDown} tone="rose" testId="kpi-expense" />
        <Stat label="Net Kâr / Zarar" value={money(k.net_profit)} icon={TrendingUp} tone={profitTone(k.net_profit)} testId="kpi-profit" />
        <Stat label="Stok Değeri" value={money(k.stock_value)} hint={`${k.stock_count} araç satılmamış`} icon={Package} testId="kpi-stock" />
        <Stat label="Alınacaklar" value={money(k.receivables)} hint={`Gecikmiş: ${money(k.overdue_receivables)}`} icon={Wallet} tone="blue" testId="kpi-receivables" />
        <Stat label="Ödenecekler" value={money(k.debts)} hint={`Gecikmiş: ${money(k.overdue_debts)}`} icon={AlertCircle} tone="amber" testId="kpi-debts" />
        <Stat label="Araç / Aktif Proje" value={`${k.vehicle_count} / ${k.active_projects}`} icon={Car} testId="kpi-counts" />
        <Stat label="Aktif Hafriyat / Bu Ay Sefer" value={`${k.active_jobs} / ${k.trips_this_month}`} icon={Truck} testId="kpi-excavation" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <Card className="p-5 lg:col-span-2 min-w-0" data-testid="chart-monthly">
          <h3 className="font-display font-semibold mb-4">Aylık Gelir / Gider</h3>
          <div className="h-72">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={data.monthly}>
                <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                <XAxis dataKey="month" {...axis} />
                <YAxis {...axis} tickFormatter={compact} />
                <Tooltip {...tip} />
                <Legend />
                <Line type="monotone" dataKey="revenue" name="Gelir" stroke="#3B82F6" strokeWidth={2} />
                <Line type="monotone" dataKey="expense" name="Gider" stroke="#EF4444" strokeWidth={2} />
                <Line type="monotone" dataKey="profit" name="Kâr" stroke="#10B981" strokeWidth={2} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </Card>
        <Card className="p-5 min-w-0" data-testid="area-breakdown">
          <h3 className="font-display font-semibold mb-4">İş Alanı Kâr/Zarar</h3>
          <div className="space-y-3">
            {data.areas.length === 0 && <p className="text-sm text-muted-foreground">Veri yok</p>}
            {data.areas.map((a) => (
              <div key={a.area} className="border-b border-border pb-3 last:border-0" data-testid={`area-${a.area}`}>
                <div className="flex justify-between text-sm font-medium"><span>{a.area}</span>
                  <span className={a.profit >= 0 ? "text-emerald-600" : "text-rose-600"}>{money(a.profit)}</span></div>
                <div className="flex justify-between text-xs text-muted-foreground mt-1">
                  <span>Gelir {money(a.revenue)}</span><span>Gider {money(a.expense)}</span></div>
              </div>
            ))}
          </div>
        </Card>
        <Card className="p-5 lg:col-span-3 min-w-0" data-testid="chart-categories">
          <h3 className="font-display font-semibold mb-4">Gider Kategorileri</h3>
          <div className="h-72">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={data.categories}>
                <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                <XAxis dataKey="category" {...axis} />
                <YAxis {...axis} tickFormatter={compact} />
                <Tooltip {...tip} />
                <Bar dataKey="value" name="Tutar" fill="#6366F1" radius={[6, 6, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </Card>
      </div>
    </div>
  );
}
